import React, { useEffect, useCallback } from "react";
import "./LanguageSelector.css";
import CustomLabel from "../../components/CustomLabel";
import CustomDropdown from "../../components/CustomDropdown";
import { useContentServiceClient } from "../../services/brainflex/ContentServiceClient";
import type { Language, Locale, Voice, GetLanguagesOutput, GetLanguageLocalesOutput, GetLocaleVoicesOutput } from "../../services/brainflex/ContentServiceClient";
import ServiceTransaction from "../../services/ServiceTransaction";
import { useLanguageSelection } from "../../contexts/LanguageSelectionContext";

interface LanguageSelectorProps {
  //onSetLanguageSelection: (context: LanguageSelection) => void;
}

const LanguageSelector: React.FC<LanguageSelectorProps> = () => {
  // State
  // const [languages, setLanguages] = React.useState<Language[]>([]);
  const [language, setLanguage] = React.useState("");
  const [locales, setLocales] = React.useState<Locale[]>([]);
  const [locale, setLocale] = React.useState("");
  const [voices, setVoices] = React.useState<Voice[]>([]);
  const [voice, setVoice] = React.useState("");
  //const [canTranslate, setCanTranslate] = React.useState(false);
  const [languages, setLanguages] = React.useState<Language[]>([]);

  // Contexts
  const { setLanguageSelection } = useLanguageSelection();

  const contentServiceClient = useContentServiceClient();

  // Fetching

  const populateLanguages = useCallback(async () => {
    const transaction: ServiceTransaction<GetLanguagesOutput> = await contentServiceClient.getContentLanguages();
    if (transaction.response.isSuccess) {
      setLanguages(transaction.response.success?.data?.languages ?? []);
    }
  }, [contentServiceClient]);
    
  const populateLocales = useCallback(async () => {
    // Is this enough or do I need to check for empty string?
    if (language && language.length > 0) {
      const transaction: ServiceTransaction<GetLanguageLocalesOutput> = await contentServiceClient.getContentLanguageLocales(language);
      if (transaction.response.isSuccess) {
        setLocales(transaction.response.success?.data?.locales ?? []);
      }
    }
  }, [contentServiceClient, language]);
  
  const populateVoices = useCallback(async () => {
    if (locale && locale.length > 0) {
      const transaction: ServiceTransaction<GetLocaleVoicesOutput> = await contentServiceClient.getContentLocaleVoices(locale);
      if (transaction.response.isSuccess) {
        setVoices(transaction.response.success?.data?.voices ?? []);
      }
    }
  }, [contentServiceClient, locale]);
  
  // Behaviour

  // Fetching languages on component mount
  React.useEffect(() => {
    populateLanguages();
  }, [populateLanguages]);
  
  // Select first language when languages changes (if you want to)
  useEffect(() => {
    // Auto-select the language when languages changes
    if (languages.length > 0) {
      const initialLanguageCode: string = "fr";
      const language = languages.find(l => l.value === initialLanguageCode);
      if (language) {
        //setLanguage(language.value);
      }
    }
  }, [languages]);

  // Fetching locales when language changes
  const handleLanguageDropdownChange = (value: string) => 
  {
    setLanguage(value);
  }

  // Fetching locales when language changes
  React.useEffect(() => {
    // reset
    setLocale("");
    setLocales([]);
    setVoices([]);
    // repopulate
    populateLocales();
  }, [language, populateLocales]);
  
  // Select first locale when locales changes
  useEffect(() => {
    // Auto-select the first locale when locales changes
    if (locales.length > 0) {
      setLocale(locales[0].value);
    }
  }, [locales]);

  const handleLocaleDropdownChange = (value: string) => 
  {
    setLocale(value);
  }

  // Fetching voices when locale changes
  React.useEffect(() => {
    // reset
    setVoice("");
    setVoices([]);
    // repopulate
    populateVoices();
  }, [locale, populateVoices]);
  
  // Select first voice when voices changes
  useEffect(() => {
    if (voices.length > 0) {
      setVoice(voices[0].value);
    }
  }, [voices]);

  const handleVoiceDropdownChange = (value: string) => 
  {
    setVoice(value);
  }

  // can translate if language state dropdown has selection
  useEffect(() => {
    //setCanTranslate(language.length > 0);
  }, [language]);

  // can speak if language dropdown has selection and voice
  useEffect(() => {
    const hasContextValue = language.length > 0 && locale.length > 0 && voice.length > 0;
    if (hasContextValue) {
      // dont call parent event any more, use context instead
      // onSetLanguageSelection({ language: language, locale: locale, voice: voice });
      setLanguageSelection({ language, locale, voice });
    } else {
      setLanguageSelection(undefined);
    }
  }, [language, locale, voice, setLanguageSelection]);

  return (
    <div className="language-context-selector">
      <CustomLabel text="Target Language:" />
      <CustomDropdown prompt="Language" value={language} options={languages} onChange={handleLanguageDropdownChange} />

      <CustomLabel text="Locale:" />
      <CustomDropdown prompt="Locale" value={locale} options={locales} onChange={handleLocaleDropdownChange} />
      
      <CustomLabel text="Voice:" />
      <CustomDropdown prompt="Voice" value={voice} options={voices} onChange={handleVoiceDropdownChange} />
    </div>

  );
};

export default LanguageSelector;